import { useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { PLANS, getCurrentPlanId, isUpgrade, type PlanId } from "@/lib/billing";
import { schedulePlanChange } from "@/lib/billing-api";

type Props = {
  /** The plan being switched to, or null when the dialog is closed. */
  targetPlanId: PlanId | null;
  onOpenChange: (open: boolean) => void;
};

function friendlyDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

/**
 * Plus <-> Premium plan switch, in either direction. The switch is queued via
 * a Stripe subscription schedule and takes effect at the end of the current
 * billing period — nothing is charged and nothing changes until then.
 */
export function SwitchPlanDialog({ targetPlanId, onOpenChange }: Props) {
  const { user, refetch } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [effectiveAt, setEffectiveAt] = useState<string | null>(null);

  const open = targetPlanId != null;
  const currentPlanId = getCurrentPlanId(user);
  const targetPlan = targetPlanId ? PLANS.find((p) => p.id === targetPlanId) ?? null : null;
  const upgrading = targetPlanId ? isUpgrade(currentPlanId, targetPlanId) : false;

  function handleOpenChange(next: boolean) {
    onOpenChange(next);
    if (!next) {
      // Reset after the close animation so the content doesn't flash on reopen.
      setTimeout(() => {
        setSubmitting(false);
        setError(null);
        setDone(false);
        setEffectiveAt(null);
      }, 200);
    }
  }

  async function handleConfirm() {
    if (!targetPlanId || targetPlanId === "starter") return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await schedulePlanChange(targetPlanId);
      setEffectiveAt(result.effectiveAt);
      setDone(true);
      refetch();
    } catch (err) {
      setError((err as Error).message ?? "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const renewsAtFriendly = friendlyDate(user?.planRenewsAt);
  const doneFriendly = friendlyDate(effectiveAt);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        overlayClassName="z-[100]"
        className="z-[101] sm:max-w-[480px] rounded-2xl border border-black/10 bg-background p-0 gap-0 shadow-xl overflow-hidden"
      >
        <div className="px-6 pt-6 pb-2">
          <DialogHeader className="space-y-3 text-left">
            <DialogTitle className="font-display text-xl font-semibold text-foreground flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              {done ? "Switch scheduled" : `Switch to ${targetPlan?.name ?? ""}`}
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-muted-foreground text-left">
              {done
                ? `You'll move to ${targetPlan?.name} on ${doneFriendly ?? "your next billing date"}. You keep your current plan until then — nothing was charged today.`
                : `You'll switch to ${targetPlan?.name}${renewsAtFriendly ? ` on ${renewsAtFriendly}` : " at the end of your current billing period"}, right when your plan renews. Nothing changes — and nothing is charged — until then.`}
            </DialogDescription>
          </DialogHeader>
        </div>

        {!done && error && (
          <div className="px-6 pb-2">
            <p className="rounded-xl border border-red-200/70 bg-red-50/70 px-4 py-2 text-sm text-red-700">
              {error}
            </p>
          </div>
        )}

        <DialogFooter className="flex-row justify-end gap-2 border-t border-black/[0.06] bg-muted/10 px-6 py-4 sm:justify-end">
          {done ? (
            <Button
              className="rounded-full px-6 h-10 text-sm font-medium"
              onClick={() => handleOpenChange(false)}
            >
              Done
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                className="rounded-full px-5 h-10 text-sm font-medium bg-muted/50 border-black/10"
                onClick={() => handleOpenChange(false)}
                disabled={submitting}
              >
                Not now
              </Button>
              <Button
                className="rounded-full px-5 h-10 text-sm font-medium"
                onClick={handleConfirm}
                disabled={submitting}
              >
                {submitting ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Scheduling…
                  </span>
                ) : upgrading ? (
                  "Confirm upgrade"
                ) : (
                  "Confirm switch"
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
